import type {ReactElement} from 'react';
import {useContext, useState} from 'react';
import {Button, IconPlacement, Size, Type} from '@Components/button/button';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {FormMakerImmutableStore} from '@FormMakerReact/form-maker.types';
import {EditorViewport} from '@FormMakerReact/form-maker.types';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {SaveType} from '@FormMakerReact/lib/form-maker-storage-library';
import {hasPersistedForm} from '@FormMakerReact/lib/form-maker-access-library';
import {redirectToSignupFormPublishWizard} from '@Libraries/email-signup-form-library';
import styles from './publish-button.module.scss';

export function PublishButton(): ReactElement {
  const {store, state, actions} = useContext(formMakerContext);
  const [isOpening, setIsOpening] = useState<boolean>(false);
  const isLoading = isOpening || state.isEditorLoading;

  const onPublishClick = async (): Promise<void> => {
    if (isOpening || state.isEditorLoading) {
      return;
    }
    setIsOpening(true);

    try {
      const saved = await actions.save({saveType: SaveType.HARD});
      if (saved) {
        redirectPersistedFormToPublishWizard(store);
      }
    } finally {
      setIsOpening(false);
    }
  };

  const getButtonText = (): string => {
    return store.isAlreadyPublished ? window.i18next.t('pmwjs_update') : window.i18next.t('pmwjs_publish');
  };

  if (state.editorViewport !== EditorViewport.DESKTOP) {
    return (
      <Icon
        icon="icon-publish-arrow"
        type={IconType.GHOST}
        shape={IconShape.SQUARE}
        className="publish-icon-hover-animation"
        size={IconSize.SIZE_ICON_20}
        ariaLabel={window.i18next.t('pmwjs_form_maker_publish_aria')}
        isLoading={isLoading}
        loaderClassName={styles.loaderWrapper}
        onClick={(): void => {
          void onPublishClick();
        }}
      />
    );
  }

  return (
    <Button
      type={Type.SECONDARY}
      size={Size.SMALL}
      customClasses="publish-icon-hover-animation"
      text={getButtonText()}
      icon="icon-publish-arrow"
      iconPlacement={IconPlacement.RIGHT}
      isLoading={isLoading}
      onClick={(): void => {
        void onPublishClick();
      }}
    />
  );
}

/** A draft's first Publish already navigated to the wizard (handleSaveResponse minted the id); only an already-persisted form redirects itself here. */
function redirectPersistedFormToPublishWizard(store: FormMakerImmutableStore): void {
  if (hasPersistedForm(store)) {
    redirectToSignupFormPublishWizard(store.idEmailSignupForm);
  }
}
